import { useState, useEffect, useContext, useMemo, useRef } from "react";
import { useNavigate } from "react-router-dom";
import { Link } from "react-router-dom";
import { useForm, Controller } from "react-hook-form";
import { yupResolver } from "@hookform/resolvers/yup";
import api from "../../api/axios";
import { registerSchema } from "../../utils/registerSchema";
import { ThemeContext } from "../../context/ThemeContext";
import Logo from "../../components/logo/Logo";
import FloatingInput from "../../components/input/FloatingInput";
import config from "../../config/config";
import CustomSelect from "../../components/customselect/CustomSelect";
import { useTranslation } from "react-i18next";
import Toast from "../../components/common/Toast";
import usePlaceAutocomplete from "../calculators/hooks/usePlaceAutocomplete";
import { useCompanyAuthPath } from "../../hooks/useCompanyAuthPath";

const Register = () => {
  const { t: trans } = useTranslation();
  const theme = useContext(ThemeContext);
  const navigate = useNavigate();
  const { companyPath } = useCompanyAuthPath();

  const [states, setStates] = useState([]);
  const [counties, setCounties] = useState([]);
  const [titleReps, setTitleReps] = useState([]);

  // Address autocomplete hook
  const {
    query: addressQuery,
    setQuery: setAddressQuery,
    suggestions: addressSuggestions,
    suggestionItemRefs: addressSuggestionRefs,
    handleChange: handleAddressChange,
    handleKeyDown: handleAddressKeyDown,
    handleBlur: handleAddressBlur,
    selectSuggestion: selectAddressSuggestion,
  } = usePlaceAutocomplete({
    country: "us",
    onSelect: (payload) => {
      // Handle address selection - populate related fields
      try {
        if (payload) {
          const address = payload.formatted_address || "";
          setValue("mailing_address", address);
          _setAddressQuery(address);
          if (payload.city) setValue("city", payload.city);
          
          // Extract zip from formatted_address if zip is empty
          let zipCode = payload.zip || "";
          if (!zipCode && payload.formatted_address) {
            const zipMatch = payload.formatted_address.match(/\b(\d{5})\b/);
            if (zipMatch) {
              zipCode = zipMatch[1];
            }
          }
          
          if (zipCode) {
            setValue("postal_code", zipCode);
          }
        }
      } catch (error) {
        console.error('Error in address onSelect:', error);
      }
    },
    page: "register",
    maincompanyid: theme.id,
    showToaster: (message, type, visible) => {
      setToaster({ message, type, visible });
      setTimeout(() => {
        setToaster((prev) => ({ ...prev, visible: false }));
      }, 2000);
    },
  });

  const _setAddressQuery = setAddressQuery;

  const schema = useMemo(
    () => registerSchema(states, counties, titleReps, trans),
    [states, counties, titleReps, trans]
  );

  const {
    register,
    handleSubmit,
    watch,
    trigger,
    control,
    setError,
    setValue,
    getValues,
    clearErrors,
    formState: { errors, isSubmitting },
  } = useForm({
    resolver: yupResolver(schema),
    mode: "onSubmit",
  });

  const watchState = watch("state");
  const watchCounty = watch("county");
  const watchPostal = watch("postal_code");
  const [selectedCountyId, setSelectedCountyId] = useState(null);

  const [toast, setToaster] = useState({
    message: "",
    type: "success",
    visible: false,
  });

  // ✅ Fetch states
  useEffect(() => {
    if (!theme?.id) return; // wait for theme to load
    const mainCompanyId = theme.id;

    const CACHE_KEY = `states_${mainCompanyId}`;
    const CACHE_TIME_KEY = `${CACHE_KEY}_time`;
    const CACHE_DURATION = 10 * 60 * 1000; // 10 minutes
    const now = Date.now();

    const cachedStates = localStorage.getItem(CACHE_KEY);
    const cachedTime = localStorage.getItem(CACHE_TIME_KEY);

    // ✅ Check if cache is valid (company + time)
    if (
      cachedStates &&
      cachedTime &&
      now - parseInt(cachedTime, 10) < CACHE_DURATION
    ) {
      try {
        const parsedStates = JSON.parse(cachedStates);
        const selectOption = { id: "", statename: trans("register_state") };
        setStates([selectOption, ...parsedStates]);
     //   console.log("🗂 Loaded states from cache:", CACHE_KEY);
        return;
      } catch (err) {
        console.warn("⚠️ Invalid cached states, refetching...");
      }
    }

    // 🚀 Fetch from API if no valid cache
    api
      .get(`/auth/states`, { params: { maincompanyid: mainCompanyId } })
      .then((res) => {
        const fetchedStates = res.data.data || [];
        const selectOption = { id: "", statename: trans("register_state") };
        setStates([selectOption, ...fetchedStates]);

        // ✅ Save per-company cache
        localStorage.setItem(CACHE_KEY, JSON.stringify(fetchedStates));
        localStorage.setItem(CACHE_TIME_KEY, now.toString());
      //  console.log("✅ States fetched and cached:", CACHE_KEY);
      })
      .catch((err) => console.error("Failed to fetch states:", err));
  }, [theme?.id, theme?.maincompanyid, trans]);

  // ✅ Fetch counties
  useEffect(() => {
    if (!watchState) {
      setCounties([]);
      return;
    }
    api
      .get(`/auth/counties`, {
        params: { maincompanyid: theme.id, stateId: watchState },
      })
      .then((res) => {
        const fetchedCounties = res.data.data || [];
        const selectOption = { id: "", countyname: trans("register_county") };
        setCounties([selectOption, ...fetchedCounties]);
      })
      .catch((err) => console.error("Failed to fetch counties:", err));
  }, [watchState, theme.id, trans]);

  useEffect(() => {
    if (selectedCountyId && counties.length) {
      setValue("county", selectedCountyId);
    }
  }, [counties, selectedCountyId, setValue]);

  // ✅ Revalidate ZIP when necessary
  useEffect(() => {
    if (watchCounty && watchPostal) {
      trigger("postal_code");
    }
  }, [watchCounty, watchPostal, trigger]);

  // ✅ Fetch title reps
  useEffect(() => {
    if (!watchState || !watchCounty) {
      setTitleReps([]);
      return;
    }
    api
      .get(`/auth/titlereps`, {
        params: {
          maincompanyid: theme.id,
          stateId: watchState,
          countyId: watchCounty,
        },
      })
      .then((res) => {
        const fetchedReps = res.data.data || [];
        const selectOption = { id: "", name: trans("register_title_rep") };
        setTitleReps([selectOption, ...fetchedReps]);
      })
      .catch((err) => {
        console.error("Error fetching title reps:", err);
        setTitleReps([]);
      });
  }, [watchState, watchCounty, theme.id, trans]);

  // ✅ Phone number formatting function
  const formatPhoneNumber = (input) => {
    const digits = input.replace(/[^0-9]/g, "").slice(0, 12);

    if (digits.length === 0) return "";

    let formatted = "";
    const length = digits.length;

    // Handle 10-digit format: XXX-XXX-XXXX
    if (length <= 10) {
      for (let i = 0; i < length; i++) {
        if (i === 3 || i === 6) formatted += "-";
        formatted += digits[i];
      }
    }
    // Handle 11-digit format: X XXX-XXX-XXXX
    else if (length === 11) {
      formatted = `${digits[0]} ${digits.substring(1, 4)}-${digits.substring(
        4,
        7
      )}-${digits.substring(7, 11)}`;
    }
    // Handle 12-digit format: XX XXX-XXX-XXXX
    else if (length === 12) {
      formatted = `${digits.substring(0, 2)} ${digits.substring(
        2,
        5
      )}-${digits.substring(5, 8)}-${digits.substring(8, 12)}`;
    }

    return formatted;
  };

  // ✅ Normalize phone number for submission
  const normalizePhoneNumber = (formattedPhone) => {
    const digits = formattedPhone.replace(/[^0-9]/g, "");

    if (digits.length === 10) {
      return `${digits.substring(0, 3)}-${digits.substring(
        3,
        6
      )}-${digits.substring(6, 10)}`;
    } else if (digits.length === 11) {
      return `${digits.substring(0, 1)} ${digits.substring(
        1,
        4
      )}-${digits.substring(4, 7)}-${digits.substring(7, 11)}`;
    } else if (digits.length === 12) {
      return `${digits.substring(0, 2)} ${digits.substring(
        2,
        5
      )}-${digits.substring(5, 8)}-${digits.substring(8, 12)}`;
    }

    return digits;
  };

  // ✅ Handle phone input change
  const handlePhoneChange = (e) => {
    const value = e.target.value;
    const formatted = formatPhoneNumber(value);
    setValue("phone", formatted, { shouldValidate: true });
  };

  // ✅ Handle phone keydown for better UX
  const handlePhoneKeyDown = (e) => {
    // Allow backspace, tab, delete, arrows, home, end
    if ([8, 9, 46, 37, 38, 39, 40, 36, 35].includes(e.keyCode)) {
      return;
    }

    // Allow numbers
    if (e.keyCode >= 48 && e.keyCode <= 57) {
      return;
    }

    // Allow numpad numbers
    if (e.keyCode >= 96 && e.keyCode <= 105) {
      return;
    }

    // Allow Ctrl/Command + A, C, V, X
    if ((e.ctrlKey || e.metaKey) && [65, 67, 86, 88].includes(e.keyCode)) {
      return;
    }

    e.preventDefault();
  };

  // ✅ Submit handler
  const onSubmit = async (data) => {
    const stateObj = states.find((s) => s.id === parseInt(data.state));
    const countyObj = counties.find((c) => c.id === parseInt(data.county));

    const payload = {
      ...data,
      phone: normalizePhoneNumber(data.phone), // Normalize phone number
      mainid: theme.id,
      gender: "M",
      state_name: stateObj?.statename,
      county_name: countyObj?.countyname,
    };

    try {
      const res = await api.post("/auth/register", payload, {
        headers: { "Content-Type": "application/json" },
      });
      if (res.status === 201) {
        navigate(companyPath("register/success"), { state: { fromRegister: true } });
      } else {
        setToaster({
          message: trans("register_failed"),
          type: "success",
          visible: true,
        });
        setTimeout(() => {
          setToaster((prev) => ({ ...prev, visible: false }));
        }, 2000);
      }
    } catch (error) {
      console.error("Error submitting form:", error);
      alert(trans("register_error"));
    }
  };

  // ✅ Blur validation handlers
  const zipControllerRef = useRef(null);
  const emailValidRef = useRef(null);
  const usernameValidRef = useRef(null);
  const postalValidRef = useRef(null);

  const handleEmailBlur = async (eOrValue) => {
    const email =
      typeof eOrValue === "string" ? eOrValue : eOrValue.target?.value;
    if (!email) return;
    try {
      const res = await api.get("/auth/check-email", {
        params: { email, maincompanyid: theme.id, user_type: "N" },
      });
      if (res.data?.status !== "success") {
        emailValidRef.current = false;
        setError("email", {
          type: "manual",
          message: trans("register_email_exists"),
        });
      } else {
        emailValidRef.current = true;
        clearErrors("email");
      }
    } catch {
      setError("email", {
        type: "manual",
        message: trans("register_email_validation_failed"),
      });
    }
  };

  const handleUsernameBlur = async (eOrValue) => {
    const username =
      typeof eOrValue === "string" ? eOrValue : eOrValue.target?.value;
    if (!username) return;
    try {
      const res = await api.get("/auth/check-username", {
        params: { username, maincompanyid: theme.id },
      });
      if (res.data?.status !== "success") {
        usernameValidRef.current = false;
        setError("username", {
          type: "manual",
          message: trans("register_username_exists"),
        });
      } else {
        usernameValidRef.current = true;
        clearErrors("username");
      }
    } catch {
      setError("username", {
        type: "manual",
        message: trans("register_username_validation_failed"),
      });
    }
  };

  const handlePostalBlur = async (eOrValue) => {
    const zip =
      typeof eOrValue === "string" ? eOrValue : eOrValue.target?.value;
    if (!zip) return;

    if (zipControllerRef.current) {
      try {
        zipControllerRef.current.abort();
      } catch {}
    }

    const controller = new AbortController();
    zipControllerRef.current = controller;

    const stateid = getValues("state");
    const countyid = getValues("county") || selectedCountyId;

    try {
      const res = await api.post(
        "/auth/verify-zip",
        { zipcode: zip, maincompanyid: theme.id, stateid, countyid },
        { signal: controller.signal }
      );
      if (res.data?.status === "fail") {
        setError("postal_code", {
          type: "manual",
          message: trans("register_zip_invalid"),
        });
      } else {
        const state = res.data?.data?.stateid;
        const county = res.data?.data?.countyid;
        setValue("state", "");
        setTimeout(() => {
          setValue("state", state);
          setSelectedCountyId(county);
          clearErrors("postal_code");
        }, 100);
      }
    } catch (err) {
      if (err.name !== "AbortError") {
        setError("postal_code", {
          type: "manual",
          message: trans("register_zip_validation_failed"),
        });
      }
    }
  };

  // ✅ Field bindings
  const emailReg = register("email");
  const usernameReg = register("username");
  const postalReg = register("postal_code");
  const phoneReg = register("phone");

  return (
    <div
      className="register-container"
      style={{
        "--bg-img": theme?.background_image
          ? `url(${config.assetsBaseUrl}/${theme.background_image})`
          : `url('images/dream-house.png')`,
      }}
    >
      <style>
        {`
          body {
            background-color: #f6f8fc;
          }
        `}
      </style>

      <div className="reg-cont-inner">
        <div className="reg-logo">
          <Logo type="secondaryWhite" size={196} />
        </div>

        <div className="reg-frm">
          <div className="reg-details-head">
            <h2>{trans("register_heading")}</h2>
            <p>{trans("register_already")}</p>
            <Link to={companyPath("")} rel="noopener noreferrer" className="heading-b">
              {trans("register_login_link")}
            </Link>
          </div>

          <form
            onSubmit={handleSubmit(async (data) => {
              if (
                emailValidRef.current === false ||
                usernameValidRef.current === false ||
                postalValidRef.current === false
              ) {
                if (emailValidRef.current === false) {
                  setError("email", {
                    type: "manual",
                    message: trans("register_email_exists"),
                  });
                }
                if (usernameValidRef.current === false) {
                  setError("username", {
                    type: "manual",
                    message: trans("register_username_exists"),
                  });
                }
                if (postalValidRef.current === false) {
                  setError("postal_code", {
                    type: "manual",
                    message: trans("register_zip_invalid"),
                  });
                }
                return;
              }

              const valid = await trigger();
              if (!valid) return;
              if (errors && Object.keys(errors).length > 0) return;

              await onSubmit(data);
            })}
          >
            <div className="reg-f-container">
              {/* First & Last Name */}
              <div className="frm-field-2">
                <div className="form-group">
                  <FloatingInput
                    label={trans("register_first_name")}
                    {...register("first_name")}
                  />
                  {errors.first_name && (
                    <p className="field-error">{errors.first_name.message}</p>
                  )}
                </div>
                <div className="form-group">
                  <FloatingInput
                    label={trans("register_last_name")}
                    {...register("last_name")}
                  />
                  {errors.last_name && (
                    <p className="field-error">{errors.last_name.message}</p>
                  )}
                </div>
              </div>

              {/* Email Fields */}
              <div className="frm-field-2">
                <div className="form-group">
                  <FloatingInput
                    label={trans("register_email")}
                    {...emailReg}
                    onBlur={handleEmailBlur}
                  />
                  {errors.email && (
                    <p className="field-error">{errors.email.message}</p>
                  )}
                </div>
                <div className="form-group">
                  <FloatingInput
                    label={trans("register_confirm_email")}
                    {...register("confirmEmail")}
                  />
                  {errors.confirmEmail && (
                    <p className="field-error">{errors.confirmEmail.message}</p>
                  )}
                </div>
              </div>

              {/* Username */}
              <div className="frm-field-1">
                <div className="form-group">
                  <FloatingInput
                    label={trans("register_username")}
                    {...usernameReg}
                    onBlur={handleUsernameBlur}
                  />
                  {errors.username && (
                    <p className="field-error">{errors.username.message}</p>
                  )}
                </div>
              </div>

              {/* Passwords */}
              <div className="frm-field-2">
                <div className="form-group">
                  <FloatingInput
                    type="password"
                    label={trans("register_password")}
                    {...register("password")}
                  />
                  {errors.password && (
                    <p className="field-error">{errors.password.message}</p>
                  )}
                </div>
                <div className="form-group">
                  <FloatingInput
                    type="password"
                    label={trans("register_confirm_password")}
                    {...register("confirmPassword")}
                  />
                  {errors.confirmPassword && (
                    <p className="field-error">
                      {errors.confirmPassword.message}
                    </p>
                  )}
                </div>
              </div>
              
              {/* Address */}
              <div className="frm-field-2">
                <div className="form-group">
                  <Controller
                    name="mailing_address"
                    control={control}
                    render={({ field }) => (
                      <FloatingInput
                        {...field}
                        label={trans("register_address")}
                        placeholder="Enter Address"
                        value={addressQuery || field.value || ""}
                        onChange={(e) => {
                          const v = e.target.value;
                          field.onChange(v);
                          handleAddressChange(v);
                          _setAddressQuery(v);
                        }}
                        onKeyDown={handleAddressKeyDown}
                        onBlur={() => {
                          handleAddressBlur();
                          field.onChange(addressQuery || field.value || "");
                        }}
                      />
                    )}
                  />
                  {addressSuggestions && addressSuggestions.length > 0 && (
                    <div className="search-list-prop">
                      {addressSuggestions.map((s, idx) => (
                        <div
                          key={idx}
                          ref={(el) => (addressSuggestionRefs.current[idx] = el)}
                          className="search-list-prop-wrap"
                          role="button"
                          tabIndex={-1}
                          onClick={async (e) => {
                            e.preventDefault();
                            e.stopPropagation();
                            try {
                              const data = await selectAddressSuggestion(s);                              
                              // Extract zip from formatted_address if zip is empty
                              let zipCode = data?.zip || "";
                              if (!zipCode && data?.formatted_address) {
                                const zipMatch = data.formatted_address.match(/\b(\d{5})\b/);
                                if (zipMatch) {
                                  zipCode = zipMatch[1];
                                }
                              }
                              
                              // Set zip code
                              if (zipCode) {
                                setValue("postal_code", zipCode);
                              }
                              
                              // Set state using stateid from enrichment
                              if (data?.enrichment?.stateid) {
                                setValue("state", data.enrichment.stateid);
                                // Set county after state is set and counties are loaded
                                if (data?.enrichment?.countyid) {
                                  setSelectedCountyId(data.enrichment.countyid);
                                }
                              }
                            } catch (error) {
                              console.error('Error selecting address:', error);
                            }
                          }}
                          onMouseDown={(e) => {
                            e.preventDefault();
                            e.stopPropagation();
                          }}
                          onTouchStart={(e) => {
                            e.preventDefault();
                            e.stopPropagation();
                          }}
                        >
                          <div className="search-list-prop-heading">
                            {s.description || s.structured_formatting?.main_text}
                          </div>
                        </div>
                      ))}
                    </div>
                  )}
                  {errors.mailing_address && (
                    <p className="field-error">
                      {errors.mailing_address.message}
                    </p>
                  )}
                </div>
                <div className="form-group">
                  <FloatingInput
                    label={trans("register_city")}
                    {...register("city")}
                  />
                  {errors.city && (
                    <p className="field-error">{errors.city.message}</p>
                  )}
                </div>
              </div>

              {/* State & County */}
              <div className="frm-field-2">
                <div className="form-group">
                  <Controller
                    name="state"
                    control={control}
                    render={({ field }) => (
                      <CustomSelect
                        options={states}
                        placeholder={trans("register_state")}
                        label={"State"}
                        onChange={field.onChange}
                        value={field.value}
                      />
                    )}
                  />
                  {errors.state && (
                    <p className="field-error">{errors.state.message}</p>
                  )}
                </div>
                <div className="form-group">
                  <Controller
                    name="county"
                    control={control}
                    render={({ field }) => (
                      <CustomSelect
                        options={counties}
                        placeholder={trans("register_county")}
                        label={"County"}
                        onChange={field.onChange}
                        value={field.value}
                      />
                    )}
                  />
                  {errors.county && (
                    <p className="field-error">{errors.county.message}</p>
                  )}
                </div>
              </div>

              {/* ZIP & Phone */}
              <div className="frm-field-2">
                <div className="form-group">
                  <FloatingInput
                    label={trans("register_zip")}
                    {...postalReg}
                    onBlur={handlePostalBlur}
                    maxLength={5}
                  />
                  {errors.postal_code && (
                    <p className="field-error">{errors.postal_code.message}</p>
                  )}
                </div>
                <div className="form-group">
                  <FloatingInput
                    label={trans("register_phone")}
                    {...phoneReg}
                    onChange={handlePhoneChange}
                    onKeyDown={handlePhoneKeyDown}
                    placeholder="XXX-XXX-XXXX"
                    maxLength={17} // Maximum length for XX XXX-XXX-XXXX format
                  />
                  {errors.phone && (
                    <p className="field-error">{errors.phone.message}</p>
                  )}
                </div>
              </div>

              {/* Office & Title Rep */}
              <div className="frm-field-2">
                <div className="form-group">
                  <FloatingInput
                    label={trans("register_office")}
                    {...register("office_name")}
                  />
                  {errors.office_name && (
                    <p className="field-error">{errors.office_name.message}</p>
                  )}
                </div>
                <div className="form-group">
                  <Controller
                    name="titlerep"
                    control={control}
                    render={({ field }) => (
                      <CustomSelect
                        options={titleReps}
                        placeholder={trans("register_title_rep")}
                        label={"Title Rep"}
                        onChange={field.onChange}
                        value={field.value}
                      />
                    )}
                  />
                  {errors.titlerep && (
                    <p className="field-error">{errors.titlerep.message}</p>
                  )}
                </div>
              </div>

              {/* BRE */}
              <div className="frm-field-2">
                <div className="form-group">
                  <FloatingInput
                    label={trans("register_bre")}
                    {...register("dre_nmls")}
                  />
                </div>
                <div className="form-group">
                  <FloatingInput
                    label={trans("register_corp_bre")}
                    {...register("corporate_nmls")}
                  />
                </div>
              </div>

              {/* Submit */}
              <div className="frm-btn">
                <button
                  type="submit"
                  disabled={isSubmitting}
                  className="submit-btn btn-bg inpt-btn"
                >
                  {isSubmitting
                    ? trans("register_submitting")
                    : trans("register_submit")}
                </button>
              </div>
            </div>
          </form>

          <div className="reg-details-head ftrlink">
            <p>{trans("register_footer_help")}</p>
            <Link to={companyPath("contact")} rel="noopener noreferrer" className="heading-b">
              {trans("register_footer_link")}
            </Link>
          </div>

          <Toast
            message={toast.message}
            type={toast.type}
            visible={toast.visible}
          />
        </div>
      </div>
    </div>
  );
};

export default Register;
